import React, { useState, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { PlusCircleIcon } from "lucide-react";
import { calculateTotalAvailable } from "../../utils/billingCalculations";

const PaymentModal = ({ show, onHide, onAddPayment, userBilling, isUserPage = false, isAdmin = false, adminUser }) => {
  const { t } = useTranslation();
  // Default to credit when not on users page, deposit when on users page
  const [paymentType, setPaymentType] = useState(isUserPage ? "deposit" : "credit");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentPercentage, setPaymentPercentage] = useState("40");
  const [paymentMethod, setPaymentMethod] = useState("tipsterpage");
  const [paymentReference, setPaymentReference] = useState("");

  console.log(userBilling);

  const maxDepositAmount = useMemo(() => {
    return calculateTotalAvailable(adminUser?.billing?.payments);
  }, [adminUser?.billing?.payments]);

  const handleAddPayment = () => {
    const formattedValues = {
      amount: parseFloat(paymentAmount) || 0,
      type: paymentType,
      ...(paymentType === 'credit' && {
        rate: parseInt(paymentPercentage, 10) || 40,
        method: String(paymentMethod || 'tipsterpage'),
        reference: String(paymentReference || '')
      })
    };

    // Basic validation
    if (formattedValues.amount <= 0) {
      return; // Could add error handling here
    }

    onAddPayment(formattedValues);
    onHide();
    
    // Reset form with defaults
    setPaymentAmount("");
    setPaymentPercentage("40");
    setPaymentMethod("tipsterpage");
    setPaymentReference("");
    setPaymentType(isUserPage ? "deposit" : "credit");
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("addCreditPayment")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Payment History Section */}
          <div className="w-full lg:w-1/2 lg:border-r border-[#FFFFFF]/20 lg:pr-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t("paymentHistory")}</h3>
            <div className="space-y-4 max-h-[200px] lg:max-h-[400px] overflow-y-auto">
              {userBilling?.payments?.length > 0 ? (
                userBilling?.payments
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((payment, index) => (
                    <div key={index} className="bg-[#2A3C29] p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <span className={`font-semibold ${payment.amount < 0 ? 'text-red-400' : 'text-[#E8FC8B]'}`}>
                          {payment.type === 'withdrawal' ? '-' : ''}{Math.abs(payment.amount).toFixed(2)}€
                        </span>
                        <span className="text-[#FFFFFF] text-sm">
                          {new Date(payment.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="text-[#FFFFFF] text-sm">
                        {t("type")}: {t(payment.type)}
                      </div>
                      {payment.method && (
                        <div className="text-[#FFFFFF] text-sm">
                          {t("paymentMethod")}: {t(payment.method)}
                        </div>
                      )}
                      {payment.reference && (
                        <div className="text-[#FFFFFF] text-sm">
                          {t("reference")}: {payment.reference}
                        </div>
                      )}
                      {payment.rate && (
                        <div className="text-[#FFFFFF] text-sm">
                          {t("rateOverProfit")}: {payment.rate}%
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <div className="text-[#FFFFFF] text-center py-4">{t("noPaymentHistory")}</div>
              )}
            </div>
          </div>

          {/* New Payment Form Section */}
          <div className="w-full lg:w-1/2">
            <h3 className="text-lg font-semibold text-white mb-4">{t("newPayment")}</h3>
            <div className="space-y-4">
              {isUserPage ? (
                <div>
                  <label className="block text-sm font-medium mb-1 text-white">{t("type")}</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                        paymentType === 'deposit'
                          ? 'bg-[#E8FC8B] text-[#1B2D1A]'
                          : 'bg-[#2A3C29] text-[#FFFFFF] hover:bg-[#3A4C39]'
                      }`}
                      onClick={() => setPaymentType('deposit')}
                    >
                      {t("deposit")}
                    </button>
                    {userBilling?.maxWithdrawalAmount < 0 && (
                      <button
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                          paymentType === 'withdrawal'
                            ? 'bg-[#E8FC8B] text-[#1B2D1A]'
                            : 'bg-[#2A3C29] text-[#FFFFFF] hover:bg-[#3A4C39]'
                        }`}
                        onClick={() => setPaymentType('withdrawal')}
                      >
                        {t("withdrawal")}
                      </button>
                    )}
                  </div>
                  {isAdmin && (
                    <button
                      className={`mt-2 w-full px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                        paymentType === 'credit'
                          ? 'bg-[#E8FC8B] text-[#1B2D1A]'
                          : 'bg-[#2A3C29] text-[#FFFFFF] hover:bg-[#3A4C39]'
                      }`}
                      onClick={() => setPaymentType('credit')}
                    >
                      {t("credit")}
                    </button>
                  )}
                </div>
              ) : (
                // When not on users page, only show credit fields
                <div>
                  <input type="hidden" value="credit" />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 text-white">
                  {paymentType === 'withdrawal' ? t("withdrawal") : t("amountPaid")}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={paymentAmount}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    onChange={(e) => setPaymentAmount(e.target.value)}
                  />
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">€</span>
                </div>
                {paymentType === 'deposit' && (
                  <div className="bg-[#2A3C29] p-2 rounded-md mt-2">
                    <div className="text-sm font-medium text-[#E8FC8B]">
                      {t("totalAvailable")}: {maxDepositAmount.toFixed(2)}€
                    </div>
                  </div>
                )}
                {paymentType === 'withdrawal' && userBilling?.maxWithdrawalAmount < 0 && (
                  <div className="bg-[#2A3C29] p-2 rounded-md mt-2">
                    <div className="text-sm font-medium text-[#E8FC8B]">
                      {t("totalAvailable")}: {Math.abs(userBilling?.maxWithdrawalAmount).toFixed(2)}€
                    </div>
                  </div>
                )}
              </div>

              {(!isUserPage || (isAdmin && paymentType === 'credit')) && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-white">{t("profitPercentage")}</label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="30"
                        value={paymentPercentage}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                        onChange={(e) => setPaymentPercentage(e.target.value)}
                      />
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-white">{t("paymentMethod")}</label>
                    <select
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <option value="" disabled>{t("selectPaymentMethod")}</option>
                      <option value="tipsterpage">Tipsterpage</option>
                      <option value="crypto">Crypto</option>
                      <option value="revolut">Revolut</option>
                      <option value="bizum">Bizum</option>
                      <option value="cash">Cash</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-white">{t("reference")} ({t("optional")})</label>
                    <input
                      type="text"
                      placeholder={t("enterReference")}
                      value={paymentReference}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                      onChange={(e) => setPaymentReference(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleAddPayment}
                  disabled={!paymentAmount || (!isUserPage && (!paymentPercentage || !paymentMethod))}
                  className="px-4 py-2 bg-[#E8FC8B] text-[#1B2D1A] hover:bg-[#E8FC8B]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-md flex items-center space-x-2"
                >
                  <PlusCircleIcon size={16} />
                  <span>{t("add")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PaymentModal; 